import { PublicationPricesPerDaySchema, WeekdayPrices } from "../../types";
/**
 * Types for price list editing with support for undefined values.
 * These types are used internally when editing price lists before validation and saving.
 */

/**
 * Weekday prices with optional/undefined values for editing.
 * All price fields can be undefined until the user fills them in.
 */
export interface WeekdayPricesEditable {
  weekday: string;
  simple_width?: number;
  double_width?: number;
  memorial?: number;
  thanks?: number;
  discount_pct?: number;
}

/**
 * Publication prices per day with editable (optional) price values.
 */
export interface PublicationPricesPerDayEditable {
  publication: PublicationPricesPerDaySchema["publication"];
  days_prices: WeekdayPricesEditable[];
  _editId?: string; // Internal ID used for React keys, not sent to API
}

/**
 * Price group with editable publication prices.
 */
export interface PricesGroupEditable {
  from_date: string;
  to_date: string;
  group_id?: string;
  publications_prices: PublicationPricesPerDayEditable[];
}

/**
 * Converts a WeekdayPricesEditable to WeekdayPrices if all required fields are defined.
 * Returns null if any required field is undefined or NaN.
 */
export function toWeekdayPrices(
  editable: WeekdayPricesEditable,
): WeekdayPrices | null {
  if (
    editable.simple_width == null ||
    editable.double_width == null ||
    editable.memorial == null ||
    editable.thanks == null ||
    editable.discount_pct == null ||
    isNaN(editable.simple_width) ||
    isNaN(editable.double_width) ||
    isNaN(editable.memorial) ||
    isNaN(editable.thanks) ||
    isNaN(editable.discount_pct)
  ) {
    return null;
  }

  return {
    weekday: editable.weekday,
    simple_width: editable.simple_width,
    double_width: editable.double_width,
    memorial: editable.memorial,
    thanks: editable.thanks,
    discount_pct: editable.discount_pct,
  };
}

/**
 * Converts a PublicationPricesPerDayEditable to PublicationPricesPerDaySchema if all days are valid.
 * Returns null if any day has missing/invalid fields or if publication is missing.
 */
export function toPublicationPricesPerDay(
  editable: PublicationPricesPerDayEditable,
): PublicationPricesPerDaySchema | null {
  if (!editable.publication || editable.days_prices.length === 0) {
    return null;
  }

  const validDaysPrices = editable.days_prices
    .map(toWeekdayPrices)
    .filter((day) => day !== null);

  if (validDaysPrices.length !== editable.days_prices.length) {
    return null;
  }

  return {
    publication: editable.publication,
    days_prices: validDaysPrices,
  };
}

/**
 * Converts editable data to schema format for day tracking purposes.
 * Unlike toPublicationPricesPerDay, this function doesn't require prices to be filled in.
 * It only tracks which days are selected for each publication, regardless of price validity.
 * Used by PublicationDaysManager to determine which days are occupied.
 */
export function toPublicationPricesPerDayForTracking(
  editable: PublicationPricesPerDayEditable,
): PublicationPricesPerDaySchema | null {
  if (!editable.publication || editable.days_prices.length === 0) {
    return null;
  }

  // Convert days, including those with undefined prices
  const daysPrices = editable.days_prices
    .filter((day) => day.weekday) // Only need a valid weekday
    .map((day) => ({
      weekday: day.weekday,
      simple_width: day.simple_width ?? 0,
      double_width: day.double_width ?? 0,
      memorial: day.memorial ?? 0,
      thanks: day.thanks ?? 0,
      discount_pct: day.discount_pct ?? 0,
    }));

  if (daysPrices.length === 0) {
    return null;
  }

  return {
    publication: editable.publication,
    days_prices: daysPrices,
  };
}
